import React, { useState } from "react";
import PropTypes from "prop-types";
import { Modal, Form, Upload, Button, message, Alert } from "antd";
import { InboxOutlined, RocketOutlined } from "@ant-design/icons";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import { AI_TIMEOUT, startAILoading } from "api/aiLoading";

const { Dragger } = Upload;
const ACCENT = "#0f5a2a";
const ALLOWED_EXT = [".pdf", ".docx", ".doc"];

function ApplyJobModal({ posting, open, onClose, onSuccess }) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const validateFile = (file) => {
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      message.error("Only PDF / DOC / DOCX files are allowed.");
      return Upload.LIST_IGNORE;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error("File must be 5 MB or smaller.");
      return Upload.LIST_IGNORE;
    }
    return false; // prevent auto-upload
  };

  const handleSubmit = async () => {
    let stopAILoading;
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // Per Swagger screenshot from Mudassir: PascalCase field names.
      // (His text said lowercase but the Swagger UI on the same endpoint
      // shows PascalCase — going with what Swagger renders.)
      const formData = new FormData();
      formData.append("JobPostingId", posting.jobPostingId || posting.id);

      const resumeFile = values.resume?.[0]?.originFileObj;
      formData.append("ResumeFile", resumeFile);

      const coverFile = values.coverLetter?.[0]?.originFileObj;
      if (coverFile) formData.append("CoverLetterFile", coverFile);

      // Internal Apply hits the AI service (talent profile + fit score),
      // so bump timeout and show rotating loading messages.
      stopAILoading = startAILoading();
      await axiosInstance.post(API.hiringJobApplicant.internalApply, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: AI_TIMEOUT,
      });

      message.success("Application submitted successfully.");
      form.resetFields();
      onSuccess?.();
    } catch (err) {
      if (err?.errorFields) return; // form validation error, already surfaced
      console.error("Error applying for job:", err);

      // Backend returns its useful message in `details` (not `message`).
      // It also wraps a duplicate-apply as 500 with details "Employee already
      // applied this job posting." — surface that clearly instead of "Failed".
      const apiMsg =
        err.response?.data?.details ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        (typeof err.response?.data === "string" ? err.response.data : null) ||
        "Failed to submit your application. Please try again.";

      const friendly =
        typeof apiMsg === "string" && /already applied/i.test(apiMsg)
          ? "You've already applied for this role."
          : apiMsg;

      message.error(typeof friendly === "string" ? friendly : "Failed to submit your application.");
    } finally {
      setSubmitting(false);
      if (stopAILoading) stopAILoading();
    }
  };

  const title = posting.jobTitle || posting.PositionName || "this role";
  const department = posting.departmentName || posting.Department || posting.department;

  return (
    <Modal
      open={open}
      title={
        <div>
          <div style={{ fontSize: "0.72rem", color: "#617e9e", fontWeight: 600, letterSpacing: 0.4 }}>
            APPLY FOR
          </div>
          <div style={{ fontSize: "1.15rem", fontWeight: 700, color: ACCENT }}>{title}</div>
          {department && (
            <div style={{ fontSize: "0.8rem", color: "#5b6f8c", marginTop: 2 }}>{department}</div>
          )}
        </div>
      }
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      footer={null}
      width={560}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        message="Your employee profile will be used to apply automatically."
        description="HR will review your application along with your current role, skills, and performance data. Please upload an up-to-date resume below."
        style={{ marginBottom: 18, borderRadius: 12 }}
      />

      <Form form={form} layout="vertical" requiredMark>
        <Form.Item
          name="resume"
          label={<span style={{ fontWeight: 600 }}>Updated resume</span>}
          valuePropName="fileList"
          getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
          rules={[
            {
              validator: (_, fileList) =>
                fileList && fileList.length > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error("Please upload your resume.")),
            },
          ]}
        >
          <Dragger
            multiple={false}
            maxCount={1}
            beforeUpload={validateFile}
            accept=".pdf,.doc,.docx"
            style={{ borderRadius: 12 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: ACCENT }} />
            </p>
            <p className="ant-upload-text" style={{ fontSize: "0.9rem" }}>
              Click or drag a resume to upload
            </p>
            <p className="ant-upload-hint" style={{ fontSize: "0.75rem" }}>
              PDF / DOC / DOCX, up to 5 MB
            </p>
          </Dragger>
        </Form.Item>

        <Form.Item
          name="coverLetter"
          label={<span style={{ fontWeight: 600 }}>Cover letter (optional)</span>}
          valuePropName="fileList"
          getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
        >
          <Dragger
            multiple={false}
            maxCount={1}
            beforeUpload={validateFile}
            accept=".pdf,.doc,.docx"
            style={{ borderRadius: 12 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: ACCENT }} />
            </p>
            <p className="ant-upload-text" style={{ fontSize: "0.9rem" }}>
              Click or drag a cover letter to upload
            </p>
            <p className="ant-upload-hint" style={{ fontSize: "0.75rem" }}>
              PDF / DOC / DOCX, up to 5 MB
            </p>
          </Dragger>
        </Form.Item>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 6 }}>
          <Button
            onClick={() => {
              form.resetFields();
              onClose();
            }}
            style={{ borderRadius: 40, paddingLeft: 22, paddingRight: 22 }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<RocketOutlined />}
            loading={submitting}
            onClick={handleSubmit}
            style={{
              background: ACCENT,
              borderColor: ACCENT,
              borderRadius: 40,
              paddingLeft: 22,
              paddingRight: 22,
              fontWeight: 600,
            }}
          >
            Submit Application
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

ApplyJobModal.propTypes = {
  posting: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default ApplyJobModal;
